import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { getAgentSummary } from "../../services/admin/BetsService.tsx";
import { useUser } from "../UserContext.tsx";
import { Users } from "../../models/User.tsx";
import { AGENT_SUMMARY_REFRESH_EVENT } from "../../helpers/agentSummaryRefresh.tsx";
import { patchHierarchyRosterUser, resolveHierarchyRoster } from "../../helpers/hierarchyRoster.ts";
import { hasBrowserSessionAlive } from "../../helpers/browserSession.ts";

type AgentSummaryView = {
    weekTotal: number;
    todayTotal: number;
    playerActive: number;
    casinoAccounts: number;
};

type AgentContextValue = {
    playerActive: number | null;
    casinoAccounts: number | null;
    totalToday: number | null;
    totalWeek: number | null;
    loading: boolean;
    allAgents: Users[];
    allPlayers: Users[];
    ensureSummary: () => void;
    refreshRoster: () => void;
    updatePlayerInContext: (playerId: string, updates: Partial<Users>) => void;
    updateAgentInContext: (agentId: string, updates: Partial<Users>) => void;
    removePlayerFromContext: (playerId: string) => void;
};

const EMPTY_SUMMARY: AgentSummaryView = {
    weekTotal: 0,
    todayTotal: 0,
    playerActive: 0,
    casinoAccounts: 0,
};

const AgentContext = createContext<AgentContextValue>({
    playerActive: null,
    casinoAccounts: null,
    totalToday: null,
    totalWeek: null,
    loading: true,
    allAgents: [],
    allPlayers: [],
    ensureSummary: () => undefined,
    refreshRoster: () => undefined,
    updatePlayerInContext: () => undefined,
    updateAgentInContext: () => undefined,
    removePlayerFromContext: () => undefined,
});

const isAgentType = (userType?: string) =>
    userType === "masterAgent" || userType === "subAgent";

/** Screens whose own Firestore reads must win the first network slots at boot. */
const PRIORITY_DATA_PATHS = ["/weekly-figures", "/pendingBets"];

const isPriorityDataPath = (path: string) =>
    PRIORITY_DATA_PATHS.some((p) => path === p || path.startsWith(`${p}/`));

/** Dedupe in-flight summary reads (Strict Mode / rapid remounts / menu ensure). */
const summaryInflight = new Map<string, Promise<AgentSummaryView>>();
const SUMMARY_TIMEOUT_MS = 8_000;
const SUMMARY_MAX_ATTEMPTS = 3;
const SUMMARY_RETRY_DELAYS_MS = [400, 1_200];
const SUMMARY_FAIL_COOLDOWN_MS = 8_000;
/** Let Auth attach the token to Firestore Lite before the first read. */
const AUTH_SETTLE_MS = 100;
/** Give Weekly/Pending a head start if the session boots on those routes. */
const PRIORITY_ROUTE_DEFER_MS = 2_000;

const delay = (ms: number) => new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
});

const isTimeoutError = (error: unknown) =>
    error instanceof Error && error.message === "summary-timeout";

const isRetryableSummaryError = (error: unknown): boolean => {
    if (isTimeoutError(error)) return true;
    const code = (error as { code?: string })?.code;
    return (
        code === "permission-denied" ||
        code === "unauthenticated" ||
        code === "unavailable" ||
        code === "deadline-exceeded" ||
        code === "resource-exhausted" ||
        code === "aborted"
    );
};

const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> =>
    new Promise<T>((resolve, reject) => {
        const timer = window.setTimeout(() => reject(new Error("summary-timeout")), ms);
        promise.then(
            (value) => {
                window.clearTimeout(timer);
                resolve(value);
            },
            (error) => {
                window.clearTimeout(timer);
                reject(error);
            }
        );
    });

const mapSummary = (data: Awaited<ReturnType<typeof getAgentSummary>>): AgentSummaryView => {
    if (!data) return EMPTY_SUMMARY;
    return {
        weekTotal: data.weekTotal ?? 0,
        todayTotal: data.todayTotal ?? 0,
        playerActive: data.activeAccounts ?? 0,
        casinoAccounts: data.casinoAccounts ?? 0,
    };
};

const readSummaryOnce = async (userName: string): Promise<AgentSummaryView> => {
    const data = await withTimeout(getAgentSummary({ userName }), SUMMARY_TIMEOUT_MS);
    return mapSummary(data);
};

const readSummaryWithRetry = async (
    userName: string,
    onFirstAttemptSettled?: () => void,
): Promise<AgentSummaryView> => {
    let firstAttemptSettled = false;
    const markFirstSettled = () => {
        if (firstAttemptSettled) return;
        firstAttemptSettled = true;
        onFirstAttemptSettled?.();
    };

    let lastError: unknown;
    for (let attempt = 0; attempt < SUMMARY_MAX_ATTEMPTS; attempt++) {
        try {
            if (attempt === 0) await delay(AUTH_SETTLE_MS);
            const next = await readSummaryOnce(userName);
            markFirstSettled();
            return next;
        } catch (error) {
            lastError = error;
            markFirstSettled();

            const maxAttempts = isTimeoutError(error) ? 2 : SUMMARY_MAX_ATTEMPTS;
            const canRetry = isRetryableSummaryError(error) && attempt + 1 < maxAttempts;
            if (!canRetry) break;

            await delay(SUMMARY_RETRY_DELAYS_MS[attempt] ?? 1_000);
        }
    }

    throw lastError;
};

const loadAgentSummary = (
    userName: string,
    onFirstAttemptSettled?: () => void,
): Promise<AgentSummaryView> => {
    const existing = summaryInflight.get(userName);
    if (existing) {
        onFirstAttemptSettled?.();
        return existing;
    }

    let promise!: Promise<AgentSummaryView>;
    promise = (async (): Promise<AgentSummaryView> => {
        try {
            return await readSummaryWithRetry(userName, onFirstAttemptSettled);
        } finally {
            if (summaryInflight.get(userName) === promise) {
                summaryInflight.delete(userName);
            }
        }
    })();

    summaryInflight.set(userName, promise);
    return promise;
};

const AgentProvider = ({ children }: { children: React.ReactNode }) => {
    const { user: currentUser, firebaseUser } = useUser();

    const [summary, setSummary] = useState<AgentSummaryView | null>(null);
    const [loading, setLoading] = useState(true);
    const [allAgents, setAllAgents] = useState<Users[]>([]);
    const [allPlayers, setAllPlayers] = useState<Users[]>([]);

    const userName = currentUser?.userName;
    const userType = currentUser?.userType;
    const isAgentUser = isAgentType(userType);
    const authUid = firebaseUser?.uid;
    const canBoot = Boolean(userName && authUid && isAgentUser);

    const currentUserRef = useRef(currentUser);
    currentUserRef.current = currentUser;
    const summaryRef = useRef(summary);
    summaryRef.current = summary;
    const summaryGen = useRef(0);
    /** Fetch summary once per successful login/session boot — not on every menu visit. */
    const summaryLoadedForUserRef = useRef<string | null>(null);
    const lastSummaryFailAtRef = useRef(0);

    const resetRoster = useCallback(() => {
        setAllAgents([]);
        setAllPlayers([]);
    }, []);

    const resetAll = useCallback((nextLoading: boolean) => {
        summaryGen.current += 1;
        summaryLoadedForUserRef.current = null;
        lastSummaryFailAtRef.current = 0;
        setSummary(null);
        resetRoster();
        setLoading(nextLoading);
    }, [resetRoster]);

    const fetchSummary = useCallback(async (name: string, silent = false, force = false) => {
        if (!force && summaryLoadedForUserRef.current === name && summaryRef.current) {
            setLoading(false);
            return;
        }

        const joining = summaryInflight.has(name);
        if (
            !force
            && !joining
            && lastSummaryFailAtRef.current > 0
            && Date.now() - lastSummaryFailAtRef.current < SUMMARY_FAIL_COOLDOWN_MS
        ) {
            setLoading(false);
            return;
        }

        const gen = joining ? summaryGen.current : ++summaryGen.current;
        if (!silent && !joining) setLoading(true);

        try {
            const next = await loadAgentSummary(name, () => {
                if (gen === summaryGen.current) setLoading(false);
            });
            if (gen !== summaryGen.current) return;
            lastSummaryFailAtRef.current = 0;
            setSummary(next);
            summaryLoadedForUserRef.current = name;
        } catch (error) {
            if (gen !== summaryGen.current) return;
            lastSummaryFailAtRef.current = Date.now();
            const code = (error as { code?: string })?.code;
            if (code !== "permission-denied") {
                console.error("Error fetching agent summary:", error);
            }
        } finally {
            if (gen === summaryGen.current) setLoading(false);
        }
    }, []);

    const ensureSummary = useCallback(() => {
        const user = currentUserRef.current;
        if (!user?.userName || !isAgentType(user.userType)) return;
        if (summaryLoadedForUserRef.current === user.userName && summaryRef.current) return;
        void fetchSummary(user.userName, true);
    }, [fetchSummary]);

    const fetchRoster = useCallback(async (force = false) => {
        const user = currentUserRef.current;
        if (!user || !isAgentType(user.userType)) return;

        try {
            const roster = await resolveHierarchyRoster(
                { userName: user.userName, userType: user.userType },
                { force, ttlMs: Number.POSITIVE_INFINITY }
            );
            setAllAgents(roster.subAgents);
            setAllPlayers(roster.players);
        } catch (error) {
            console.error("Error fetching hierarchy roster:", error);
        }
    }, []);

    const refreshRoster = useCallback(() => {
        void fetchRoster(true);
    }, [fetchRoster]);

    // Boot once per session (login / full refresh). Navigating to the menu does not re-fetch.
    useEffect(() => {
        if (!userName || !authUid || !hasBrowserSessionAlive()) {
            resetAll(true);
            return;
        }
        if (!isAgentUser) {
            resetAll(false);
            return;
        }

        // Already loaded for this user in this SPA session — keep showing cached summary.
        if (summaryLoadedForUserRef.current === userName && summary) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        const onPriority = isPriorityDataPath(
            typeof window !== "undefined" ? window.location.pathname : "/"
        );
        const delayMs = onPriority ? PRIORITY_ROUTE_DEFER_MS : 0;
        if (onPriority) setLoading(false);

        const timer = window.setTimeout(() => {
            if (cancelled) return;
            void fetchSummary(userName, onPriority);
            void fetchRoster();
        }, delayMs);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
        // Intentionally omit path — menu navigation must not re-trigger summary.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userName, authUid, isAgentUser, fetchSummary, fetchRoster, resetAll]);

    // Refresh when user/tree is edited (requestAgentSummaryRefresh).
    useEffect(() => {
        if (!canBoot || !userName) return;

        const onRefresh = () => {
            void fetchSummary(userName, true, true);
            void fetchRoster(true);
        };

        window.addEventListener(AGENT_SUMMARY_REFRESH_EVENT, onRefresh);
        return () => window.removeEventListener(AGENT_SUMMARY_REFRESH_EVENT, onRefresh);
    }, [canBoot, userName, fetchSummary, fetchRoster]);

    // If the first read raced auth, retry when the tab is focused again — no extra
    // reads after a successful load.
    useEffect(() => {
        if (!canBoot || !userName) return;

        const retryIfNeeded = () => {
            if (document.visibilityState !== "visible") return;
            if (summaryLoadedForUserRef.current === userName && summaryRef.current) return;
            void fetchSummary(userName, true);
        };

        window.addEventListener("focus", retryIfNeeded);
        document.addEventListener("visibilitychange", retryIfNeeded);
        return () => {
            window.removeEventListener("focus", retryIfNeeded);
            document.removeEventListener("visibilitychange", retryIfNeeded);
        };
    }, [canBoot, userName, fetchSummary]);

    const updatePlayerInContext = useCallback((playerId: string, updates: Partial<Users>) => {
        patchHierarchyRosterUser(playerId, updates);
        setAllPlayers(prev =>
            prev.map(player => (player.id === playerId ? { ...player, ...updates } : player))
        );
    }, []);

    const updateAgentInContext = useCallback((agentId: string, updates: Partial<Users>) => {
        patchHierarchyRosterUser(agentId, updates);
        setAllAgents(prev =>
            prev.map(agent => (agent.id === agentId ? { ...agent, ...updates } : agent))
        );
    }, []);

    const removePlayerFromContext = useCallback((playerId: string) => {
        setAllPlayers(prev => prev.filter(player => player.id !== playerId));
    }, []);

    const value = useMemo<AgentContextValue>(() => ({
        playerActive: summary?.playerActive ?? null,
        casinoAccounts: summary?.casinoAccounts ?? null,
        totalToday: summary?.todayTotal ?? null,
        totalWeek: summary?.weekTotal ?? null,
        loading,
        allAgents,
        allPlayers,
        ensureSummary,
        refreshRoster,
        updatePlayerInContext,
        updateAgentInContext,
        removePlayerFromContext,
    }), [summary, loading, allAgents, allPlayers, ensureSummary, refreshRoster, updatePlayerInContext, updateAgentInContext, removePlayerFromContext]);

    return (
        <AgentContext.Provider value={value}>
            {children}
        </AgentContext.Provider>
    );
};

export const useAgent = () => useContext(AgentContext);

export { AgentProvider };
export default AgentContext;
